import { AnimatePresence, motion } from "motion/react";
import styled from "@emotion/styled";
import { MDSBubble, MDSBubbleAlignment, MDSIcon, MDSIconColor, MDSIconKind, MDSLink } from "@/mds";
import type { AudioCaptureTranscriptState } from "../audio-capture-actions.types";
import { MutableRefObject, type MouseEvent, useEffect, useState } from "react";
import { isMac } from "@/domains/platform/isMac";
import { isWin } from "@/domains/platform/isWin";
import {
  AudioCaptureTranscriptViewMode,
  getAudioCaptureTranscriptViewState,
} from "./audio-capture-transcript.model";
import { AudioCapturePhase } from "@/store/audio/pipeline/audio-capture.types";

interface AudioCaptureTranscriptProps {
  transcript: AudioCaptureTranscriptState;
  isAdmin: boolean;
  isTextTranscript: boolean;
  onToggleAdminMode: () => void;
  transcriptContainerRef: MutableRefObject<HTMLDivElement | null>;
  onTranscriptScroll: () => void;
  isMicrophoneDisconnected: boolean;
  isAudioSilent: boolean;
  phase: AudioCapturePhase;
  shouldShowDesktopDownloadNotice: boolean;
  onDownloadClick: () => void;
  onOpenMicrophoneSystemPermissions: () => void;
  audioRecordingFailureMessage?: string | null;
}

export function AudioCaptureTranscript({
  transcript,
  isAdmin,
  isTextTranscript,
  onToggleAdminMode,
  transcriptContainerRef,
  onTranscriptScroll,
  isMicrophoneDisconnected,
  isAudioSilent,
  phase,
  shouldShowDesktopDownloadNotice,
  onDownloadClick,
  onOpenMicrophoneSystemPermissions,
  audioRecordingFailureMessage,
}: AudioCaptureTranscriptProps) {
  const [showMicrophoneAccessNotice, setShowMicrophoneAccessNotice] = useState(false);
  const viewState = getAudioCaptureTranscriptViewState({
    transcript,
    phase,
    isAudioSilent,
    audioRecordingFailureMessage,
  });

  useEffect(() => {
    if (phase !== AudioCapturePhase.Initializing) {
      setShowMicrophoneAccessNotice(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShowMicrophoneAccessNotice(true);
    }, 10_000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [phase]);

  const handleTranscriptClick = (event: MouseEvent<HTMLDivElement>) => {
    if (isAdmin && event.altKey) {
      onToggleAdminMode();
    }
  };

  if (viewState.mode === AudioCaptureTranscriptViewMode.Transcript) {
    return (
      <StyledTranscriptContainer ref={transcriptContainerRef} onScroll={onTranscriptScroll}>
        <StyledTranscriptContent onClick={handleTranscriptClick}>
          {isTextTranscript && <span>{transcript.fullText}</span>}

          {!isTextTranscript &&
            transcript.entries.map(entry => (
              <StyledBubble
                key={entry.id}
                align={entry.alignment}
                variant={entry.variant}
                isLastInGroup={entry.isLastInGroup}
              >
                {entry.text}
              </StyledBubble>
            ))}
          {viewState.showDisconnected && (
            <TranscriptStatus>
              <MDSIcon size={12} kind={MDSIconKind.CloudSlash} color={MDSIconColor.Secondary} />
              <span>Transcription disconnected, reconnecting...</span>
            </TranscriptStatus>
          )}
          {viewState.showCompromised && (
            <TranscriptStatus>
              <MDSIcon size={12} kind={MDSIconKind.Warning} color={MDSIconColor.Danger} />
              <span>
                Transcription disconnected in the middle of recording. We&apos;ll automatically
                regenerate it after you upload the recording.
              </span>
            </TranscriptStatus>
          )}
          {viewState.showFailureMessage && (
            <TranscriptStatus>
              <MDSIcon size={12} kind={MDSIconKind.CircleX} color={MDSIconColor.Danger} />
              <RedTextColor>{audioRecordingFailureMessage}</RedTextColor>
            </TranscriptStatus>
          )}
          <AudioCaptureIssues
            isAudioSilent={viewState.shouldShowAudioSilentWarning}
            isMicrophoneDisconnected={isMicrophoneDisconnected}
          />
        </StyledTranscriptContent>
        {shouldShowDesktopDownloadNotice && (
          <AudioCaptureDownloadNotice onDownloadClick={onDownloadClick} />
        )}
      </StyledTranscriptContainer>
    );
  }

  if (viewState.mode === AudioCaptureTranscriptViewMode.StatusOnly) {
    return (
      <StyledTranscriptContainer>
        {viewState.showTranscriptionUnsupported && (
          <TranscriptStatus>
            <MDSIcon size={12} kind={MDSIconKind.TextSlash} color={MDSIconColor.Danger} />
            <RedTextColor>Transcription not supported on this application</RedTextColor>
          </TranscriptStatus>
        )}
        {viewState.showDisconnected && (
          <TranscriptStatus>
            <MDSIcon size={12} kind={MDSIconKind.CloudSlash} color={MDSIconColor.Secondary} />
            <span>Transcription disconnected, reconnecting...</span>
          </TranscriptStatus>
        )}
        {viewState.showCompromised && (
          <TranscriptStatus>
            <MDSIcon size={12} kind={MDSIconKind.Warning} color={MDSIconColor.Danger} />
            <span>
              Transcription disconnected in the middle of recording. We&apos;ll automatically
              regenerate it after you upload the recording.
            </span>
          </TranscriptStatus>
        )}
        {viewState.showFailureMessage && (
          <TranscriptStatus>
            <MDSIcon size={12} kind={MDSIconKind.CircleX} color={MDSIconColor.Danger} />
            <RedTextColor>{audioRecordingFailureMessage}</RedTextColor>
          </TranscriptStatus>
        )}

        {viewState.showListeningStatus && (
          <TranscriptStatus>
            <MDSIcon size={12} kind={MDSIconKind.Microphone} color={MDSIconColor.Secondary} />
            <span>
              {phase === AudioCapturePhase.Initializing ? "Starting Voice Mode..." : "Listening..."}
            </span>
          </TranscriptStatus>
        )}
        <AudioCaptureInitializationNotice
          shouldShow={showMicrophoneAccessNotice}
          onOpenMicrophoneSystemPermissions={onOpenMicrophoneSystemPermissions}
        />
        <AudioCaptureIssues
          isAudioSilent={viewState.shouldShowAudioSilentWarning}
          isMicrophoneDisconnected={isMicrophoneDisconnected}
        />
        {shouldShowDesktopDownloadNotice && (
          <AudioCaptureDownloadNotice onDownloadClick={onDownloadClick} />
        )}
      </StyledTranscriptContainer>
    );
  }

  if (viewState.mode === AudioCaptureTranscriptViewMode.FailureOnly) {
    return (
      <StyledTranscriptContainer>
        <TranscriptStatus>
          <MDSIcon size={12} kind={MDSIconKind.CircleX} color={MDSIconColor.Danger} />
          <RedTextColor>{audioRecordingFailureMessage}</RedTextColor>
        </TranscriptStatus>
        <AudioCaptureIssues
          isAudioSilent={viewState.shouldShowAudioSilentWarning}
          isMicrophoneDisconnected={isMicrophoneDisconnected}
        />
        {shouldShowDesktopDownloadNotice && (
          <AudioCaptureDownloadNotice onDownloadClick={onDownloadClick} />
        )}
      </StyledTranscriptContainer>
    );
  }

  return null;
}

interface AudioCaptureIssuesProps {
  isAudioSilent: boolean;
  isMicrophoneDisconnected: boolean;
}

function AudioCaptureIssues({ isAudioSilent, isMicrophoneDisconnected }: AudioCaptureIssuesProps) {
  return (
    <>
      <AnimatePresence>
        {isAudioSilent && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0, transition: { duration: 0.2, ease: "easeInOut" } }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <TranscriptStatus>
              <RedTextColor>No audio detected, please check your microphone.</RedTextColor>
            </TranscriptStatus>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isMicrophoneDisconnected && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{
              height: 0,
              opacity: 0,
              transition: { delay: 0.8, duration: 0.2, ease: "easeInOut" },
            }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <TranscriptStatus>
              <MDSIcon
                size={12}
                kind={MDSIconKind.MicrophoneSlash}
                color={MDSIconColor.Secondary}
              />
              <span>Microphone was disconnected, restarting voice mode...</span>
            </TranscriptStatus>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

interface AudioCaptureInitializationNoticeProps {
  shouldShow: boolean;
  onOpenMicrophoneSystemPermissions: () => void;
}

function AudioCaptureInitializationNotice({
  shouldShow,
  onOpenMicrophoneSystemPermissions,
}: AudioCaptureInitializationNoticeProps) {
  const isMacPlatform = isMac();
  const isWindowsPlatform = isWin();

  if (!isMacPlatform && !isWindowsPlatform) {
    return null;
  }

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0, transition: { duration: 0.2, ease: "easeInOut" } }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          <TranscriptStatus>
            <MDSIcon size={12} kind={MDSIconKind.Warning} color={MDSIconColor.Danger} />
            {isMacPlatform ? (
              <span>
                We&apos;re having trouble accessing your microphone. Make sure that you&apos;ve
                granted Mem microphone access in{" "}
                <MDSLink onPress={onOpenMicrophoneSystemPermissions}>System Permissions</MDSLink>.
              </span>
            ) : (
              <span>
                We&apos;re having trouble accessing your microphone. Please try again later.
              </span>
            )}
          </TranscriptStatus>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

interface AudioCaptureDownloadNoticeProps {
  onDownloadClick: () => void;
}

function AudioCaptureDownloadNotice({ onDownloadClick }: AudioCaptureDownloadNoticeProps) {
  return (
    <DownloadDesktopAppNoticeOnWebContainer>
      <TranscriptStatus>
        <MDSIcon size={12} kind={MDSIconKind.Warning} color={MDSIconColor.Secondary} />
        <span>
          Web only transcribes what your microphone hears.{" "}
          <MDSLink onPress={onDownloadClick}>Download the Mem desktop app</MDSLink> to transcribe
          all system audio and capture complete meeting notes.
        </span>
      </TranscriptStatus>
    </DownloadDesktopAppNoticeOnWebContainer>
  );
}

const StyledTranscriptContainer = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing["4"]};
  overflow-y: auto;
  max-height: max(100px, 50vh);
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
  scrollbar-width: none;
  width: 100%;
`;

const StyledTranscriptContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["1"]};
  font-size: ${({ theme }) => theme.fontSizes["paragraph-s"]};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: ${({ theme }) => theme.lineHeights["paragraph-sm"]};
  white-space: pre-wrap;
`;

const TranscriptStatus = styled.div`
  align-items: flex-start;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  flex-direction: row;
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  gap: ${({ theme }) => theme.spacing["1"]};
  padding-left: ${({ theme }) => theme.spacing["2"]};
  line-height: ${({ theme }) => theme.lineHeights["text-s"]};
  width: 100%;
`;

const RedTextColor = styled.span`
  color: ${({ theme }) => theme.colors.text.danger};
`;

const StyledBubble = styled(MDSBubble)`
  & > * {
    max-width: unset;

    ${({ align, theme }) =>
      align === MDSBubbleAlignment.Left
        ? `margin-right: ${theme.spacing[5]};`
        : `margin-left: ${theme.spacing[5]};`};
  }
`;

const DownloadDesktopAppNoticeOnWebContainer = styled.div`
  padding: ${({ theme }) => `${theme.spacing["2"]} ${theme.spacing["4"]} ${theme.spacing["2"]} 0`};
`;
